import { periodicCadence } from '../eval/status-timeline.js';
/**
 * The rule: the price counts what the runtime releases. Where the price caps truncation exactly, the cast is allowed and priced
 * at the cap; where it cannot, a cast whose worst-case releases can exceed the budget is refused (`CAST_BUDGET_TRUNCATION`).
 * The runtime spends one counter across every listener, in listener order, while `castStageHits` caps each plain event stage
 * by itself at the whole `unitLimit`. That cap is exact only when (a) the cast's worst-case demand cannot exceed the budget,
 * so nothing is ever cut, or (b) the one thing that can be cut is a single plain event stage (`ON_STAGE_EVENT`, no
 * MARKED/CARRIERS sweep, no chain, no status hook, no `secondary_attack` anywhere in the cast's effect graphs, no skill status,
 * and no other event stage): its cap is then the remaining budget, whatever the order (`capsExactly`). Every other cast that
 * can be cut is refused.
 *
 * The worst-case attack-unit demand of a cast against the one budget the runtime
 * spends it from: chain members, event
 * releases, hooks and carrier sweeps are counted together here, for a canonical skill, a program of the skill language
 * (`castBudgetTruncationOfStages`) and the host's execution limit alike.
 *
 * `executeCompositeCast` gives a cast a single budget (`identity.budget`, `eventUnitLimit(plain units, attack limit)`) and
 * charges every event release, every hook release and every release at a carrier of a sweep to it, in the order they
 * happen; a release that does not fit is dropped.
 *
 * The demand is bounded from the plan alone:
 *
 * ```text
 * demand = sum over stages released by something of  (most releases of the stage) x (units of one release)
 * ```
 *
 * - a hook at a status is released at most once per carrier, per instance (apply, end) or per timer firing (tick); an
 *   enemy has at most one instance per lifetime of the status (instances are a lifetime apart), and fires its timer at
 *   most once per interval of the time it is held;
 * - a sweep is released once per carrier, so at most the cast's target bound times the sweeps its cause makes;
 * - an event stage is released at most as often as its event happens: per cast once, per unit once per unit, per hit at
 *   most the target bound times the contacts a unit can make;
 * - a `secondary_attack` of a stage's effect graph is charged to the same budget (`executeSecondaryAttack`), once per run
 *   of the graph. A projectile or a weapon sweep runs it once per unit; a zone runs it at every pulse, an orbit at every contact
 *   of every enemy (`StageFacts.reruns`). A secondary's own graph is run again the same way by the secondary's delivery: a
 *   persistent zone at every pulse (`spawnPersistentArea`), anything else once. So: the secondaries of the graph, each with its
 *   own nested ones times its delivery's reruns, per rerun, per unit, per release, of every stage, a plain one included.
 *
 * Carriers are bounded by `CAST_TARGET_BOUND`; the plan cannot say how many enemies stand in an area (areas carry no target
 * cap), so the bound is a stated assumption, not a measurement.
 */
import { ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ } from '../constraints/technical.js';
import { deliveryApplicationSchedule } from '../eval/persistence.js';
import { resolveLegacyDeliveryForApplication } from './application.js';
import { eventUnitLimit, maxContactsPerUnit } from './composite-event-bound.js';
import type { AttackStageV1, CanonicalSkillV3Input, SkillStatusDefinitionV1, StageTimingV1 } from './composite-types.js';
import { expandSpatialPattern } from './spatial-pattern.js';
import type { SkillApplicationV2 } from './types.js';

/** The refusal for a cast whose worst-case releases can exceed the cast's attack budget. */
export const CAST_BUDGET_TRUNCATION = 'CAST_BUDGET_TRUNCATION';

/**
 * The most enemies one cast is taken to reach at once: the carriers a sweep or a hook is released at. PROVISIONAL: equal to
 * the field's per-camp enemy cap (`FIELD_MAX_ENEMIES_PER_CAMP` / 3); areas carry no target cap, so the plan has no bound of its own.
 */
export const CAST_TARGET_BOUND = 8;

export interface CastBudgetDemand {
  /** The attack units the cast's follow-up stages can release, worst case. */
  readonly demand: number;
  /** What the cast's budget has for them: the attack limit less the plain stages' units. */
  readonly limit: number;
  /** Set when a stage's most releases cannot be bounded from the plan. */
  readonly unbounded?: string;
}

/** What the demand is counted from, for one stage: the same facts whether they come from a canonical stage or a written program. */
export interface StageFacts {
  readonly stageId: string;
  readonly timing: StageTimingV1;
  /** Whether the stage is a chain formation: its units are the members of every volley. */
  readonly chain: boolean;
  readonly units: number;
  readonly lastUnitS: number;
  /** Seconds the stage's contacts run on after its release. */
  readonly runS: number;
  /** Applications a unit makes to one enemy: the delivery's pulses, or an orbit's one per tick. */
  readonly applications: number;
  /** Times a unit runs the stage's effect graph afresh (a secondary attack spends budget each time): a projectile runs it once; an area re-runs it every pulse, an orbit at every contact of every enemy. */
  readonly reruns: number;
  /** Enemies one unit can strike in one application: a bolt that stops at its first contact strikes one, an area strikes up to the target bound. */
  readonly reach: number;
  readonly creates: readonly string[];
  /** Budget units one run of the stage's effect graph spends on `secondary_attack` nodes (`countSecondaryAttacks`). */
  readonly secondaries: number;
}

/**
 * The budget units one run of an effect graph spends on `secondary_attack` nodes, through conditions and triggers: each
 * secondary one, plus its own graph's spend times the runs its delivery makes of that graph (`applicationFacts(...).reruns`:
 * a persistent zone every pulse, anything else once).
 */
export function countSecondaryAttacks(effects: readonly unknown[]): number {
  let count = 0;
  for (const node of effects as readonly { readonly kind?: unknown; readonly children?: unknown; readonly effects?: unknown; readonly application?: SkillApplicationV2 }[]) {
    if (node.kind === 'secondary_attack') {
      const reruns = node.application === undefined ? 1 : applicationFacts(node.application, false).reruns;
      count += 1 + reruns * countSecondaryAttacks(Array.isArray(node.effects) ? node.effects : []);
    } else if (node.kind === 'condition' || node.kind === 'trigger') count += countSecondaryAttacks(Array.isArray(node.children) ? node.children : []);
  }
  return count;
}

/**
 * What an attack's application makes of its releases: how long its contacts run, how many times it applies to one enemy and how
 * many enemies one unit strikes. The one derivation, for a canonical stage and for an attack of the skill language alike
 * (`orbit`: the stage moves on a path, so it touches once a tick).
 */
export function applicationFacts(application: SkillApplicationV2, orbit: boolean): Pick<StageFacts, 'runS' | 'applications' | 'reruns' | 'reach'> {
  const lowered = resolveLegacyDeliveryForApplication(application);
  let runS = 0;
  let applications = 1;
  let reach = CAST_TARGET_BOUND;
  if (orbit) {
    const lifecycle = application.lifecycle as { readonly durationS?: number };
    runS = lifecycle.durationS ?? 0;
    applications = Math.max(1, Math.ceil(runS * ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ));
  } else if (lowered.ok) {
    const delivery = lowered.delivery;
    if (delivery.type === 'projectile' && delivery.speedMps > 0) runS = delivery.rangeM / delivery.speedMps;
    else if (delivery.type === 'ground_aoe' && delivery.durationS !== undefined) runS = delivery.durationS;
    applications = deliveryApplicationSchedule(delivery, 1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ).offsetsS.length;
    reach = Math.min(reach, maxContactsPerUnit(application));
  }
  const projectile = !orbit && lowered.ok && lowered.delivery.type === 'projectile';
  return { runS, applications, reruns: projectile ? 1 : applications * (orbit ? reach : 1), reach };
}

function factsOf(stage: AttackStageV1): StageFacts {
  const expanded = expandSpatialPattern(stage.pattern, 0);
  const units = expanded.ok ? expanded.units : [];
  return {
    stageId: stage.stageId, timing: stage.timing, chain: stage.pattern.formation.kind === 'CHAIN', units: units.length,
    lastUnitS: Math.max(0, ...units.map((unit) => unit.releaseOffsetTicks)) / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ,
    ...applicationFacts(stage.application, stage.motion !== undefined),
    creates: (stage.effects as readonly { readonly kind?: unknown; readonly name?: unknown }[]).filter((node) => node.kind === 'status').map((node) => String(node.name)),
    secondaries: countSecondaryAttacks(stage.effects),
  };
}

/** Whether the price caps the cast's truncation exactly however much it demands: its one budget consumer beyond plain units is a single plain event stage. */
function capsExactly(facts: readonly StageFacts[], statuses: readonly SkillStatusDefinitionV1[]): boolean {
  return statuses.length === 0
    && facts.filter((fact) => fact.timing.kind === 'ON_STAGE_EVENT').length === 1
    && facts.every((fact) =>
      !fact.chain && fact.secondaries === 0 && fact.timing.kind !== 'ON_STATUS'
      && (fact.timing.kind !== 'ON_STAGE_EVENT' || (fact.timing.anchor !== 'MARKED' && fact.timing.anchor !== 'CARRIERS')));
}

/**
 * The attack units the cast's follow-up stages can demand at most, and the budget they draw on. `cap` is the attack limit
 * the runtime gives the cast (`Math.min(maxSpawnedAttacks, castReachLimits(rarity).maxAttackUnits)`).
 */
export function castBudgetDemand(skill: CanonicalSkillV3Input, cap: number): CastBudgetDemand {
  return castBudgetDemandOf(skill.stages.map(factsOf), skill.statuses ?? [], cap);
}

/** `castBudgetDemand` from the stage facts themselves. */
export function castBudgetDemandOf(facts: readonly StageFacts[], statusDefinitions: readonly SkillStatusDefinitionV1[], cap: number): CastBudgetDemand {
  const byId = new Map(facts.map((fact) => [fact.stageId, fact] as const));
  const followsUp = (fact: StageFacts): boolean => fact.timing.kind === 'ON_STAGE_EVENT' || fact.timing.kind === 'ON_STATUS';
  const limit = eventUnitLimit(facts.reduce((sum, fact) => sum + (followsUp(fact) ? 0 : fact.units), 0), cap);
  const T = CAST_TARGET_BOUND;
  const statuses = new Map(statusDefinitions.map((status) => [status.name, status] as const));

  // Most releases of each stage, the latest second its contacts can land, and the latest second its releases can start.
  const releases = new Map<string, number>();
  const latest = new Map<string, number>();
  const unbounded = new Set<string>();
  for (const fact of facts) {
    releases.set(fact.stageId, 1);
    latest.set(fact.stageId, 0);
  }
  const creatorsOf = (name: string): readonly StageFacts[] => facts.filter((fact) => fact.creates.includes(name));
  /** The latest a status can end: its last contact plus its lifetime, or the end of the cast's own work when it has none. */
  const lastEndS = (status: SkillStatusDefinitionV1): number => {
    const contact = creatorsOf(status.name).reduce((max, fact) => Math.max(max, latest.get(fact.stageId)!), 0);
    if (status.durationS !== undefined) return contact + status.durationS;
    const until = status.until === undefined ? undefined : latest.get(status.until.stageId);
    return Math.max(contact, until ?? 0, ...facts.filter((fact) => !followsUp(fact)).map((fact) => latest.get(fact.stageId)!));
  };
  /** Contacts one enemy can get from the stage's releases. */
  const contactsOnOne = (fact: StageFacts): number => releases.get(fact.stageId)! * fact.units * fact.applications;
  /** The most distinct enemies the stage's releases strike: every unit strikes up to its reach, and the cast reaches no more than the target bound. */
  const strikes = (fact: StageFacts): number => Math.min(T, releases.get(fact.stageId)! * fact.units * fact.reach);
  /** The most enemies that carry the status: those its creating stages strike. */
  const carriersOf = (name: string): number => Math.min(T, creatorsOf(name).reduce((sum, fact) => sum + strikes(fact), 0));
  const episodesOnOne = (status: SkillStatusDefinitionV1): number => {
    const contacts = creatorsOf(status.name).reduce((sum, fact) => sum + contactsOnOne(fact), 0);
    if (status.durationS === undefined) return 1;
    // Instances on one enemy are more than a lifetime apart, so no more than one per lifetime of the time contacts run over.
    const span = creatorsOf(status.name).reduce((max, fact) => Math.max(max, latest.get(fact.stageId)!), 0);
    return Math.max(1, Math.min(contacts, 1 + Math.floor(span / (status.durationS + 1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ))));
  };
  const firingsOnOne = (status: SkillStatusDefinitionV1): number | undefined => {
    if (status.durationS === undefined || status.tickS === undefined) return undefined;
    const cadence = periodicCadence({ durationS: status.durationS, tickS: status.tickS }, 1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ);
    const contacts = creatorsOf(status.name).reduce((sum, fact) => sum + contactsOnOne(fact), 0);
    const span = creatorsOf(status.name).reduce((max, fact) => Math.max(max, latest.get(fact.stageId)!), 0);
    // Held for no longer than every contact extends it by a lifetime, nor than the time contacts run over plus one lifetime.
    return Math.floor((Math.min(contacts * cadence.durationS, span + cadence.durationS) + 1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ) / cadence.tickS);
  };

  // Each pass settles one more level of the chain of stages that feed one another; a chain that never settles is unbounded.
  const passes = facts.length + 3;
  let changed = true;
  for (let pass = 0; pass < passes && changed; pass += 1) {
    changed = false;
    for (const fact of facts) {
      const { timing } = fact;
      let count = 1;
      let startS = 0;
      if (timing.kind === 'AFTER_RELEASE') startS = timing.offsetTicks / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ;
      else if (timing.kind === 'AT_STAGE_FRACTION') {
        const reference = byId.get(timing.referenceStageId);
        startS = reference === undefined ? 0 : latest.get(reference.stageId)!;
      } else if (timing.kind === 'ON_STATUS') {
        const status = statuses.get(timing.statusName);
        if (status === undefined) { count = T; } else {
          const per = timing.event === 'TICK' ? firingsOnOne(status) : episodesOnOne(status);
          if (per === undefined) unbounded.add(`stage ${fact.stageId} answers the timer of status ${status.name}, whose firings are not bounded by a lifetime`);
          count = carriersOf(status.name) * (per ?? 0);
          startS = lastEndS(status) + Math.max(1, timing.delayTicks ?? 0) / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ;
        }
      } else if (timing.kind === 'ON_STAGE_EVENT') {
        const reference = byId.get(timing.referenceStageId);
        const refReleases = reference === undefined ? 1 : releases.get(reference.stageId)!;
        const refUnits = reference?.units ?? 1;
        const refReach = reference?.reach ?? T;
        const refContacts = refUnits * refReleases * refReach * (reference?.applications ?? 1);
        const distinct = reference === undefined ? T : strikes(reference);
        const triggers =
          timing.per === 'CAST' ? (timing.event === 'EVERY_N' ? Math.floor(refContacts / (timing.n ?? 2)) : 1)
          : timing.event === 'END' || timing.per === 'UNIT' ? refUnits * refReleases
          : timing.event === 'KILL' ? distinct
          : timing.per === 'TARGET' ? refReleases * Math.min(T, refUnits * refReach)
          : timing.event === 'EVERY_N' ? Math.floor(refContacts / (timing.n ?? 2))
          : refContacts;
        // A sweep is released once at every carrier: of a skill status, or of the marks the reference left.
        count = timing.anchor === 'CARRIERS' ? carriersOf(timing.statusName ?? '') * triggers : timing.anchor === 'MARKED' ? distinct * triggers : triggers;
        startS = (reference === undefined ? 0 : latest.get(reference.stageId)!) + (timing.delayTicks ?? 0) / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ;
      }
      const end = startS + fact.lastUnitS + fact.runS;
      if (count !== releases.get(fact.stageId) || end !== latest.get(fact.stageId)) {
        releases.set(fact.stageId, count);
        latest.set(fact.stageId, end);
        changed = true;
      }
    }
  }
  if (changed) unbounded.add('the stages release one another in a chain that does not settle');

  const demand = facts.reduce((sum, fact) => {
    const released = releases.get(fact.stageId)! * fact.units;
    return sum + (followsUp(fact) ? released : 0) + fact.secondaries * fact.reruns * released;
  }, 0);
  return { demand, limit, ...(unbounded.size === 0 ? {} : { unbounded: [...unbounded].join('; ') }) };
}

/**
 * Why the cast can have releases dropped by its shared budget, or `undefined` when it cannot.
 * `cap` is the attack limit the runtime gives the cast: `Math.min(maxSpawnedAttacks, castReachLimits(rarity).maxAttackUnits)`,
 * narrowed by the executing host's own limit where it has one.
 */
export function castBudgetTruncation(skill: CanonicalSkillV3Input, cap: number): string | undefined {
  return castBudgetTruncationOfStages(skill.stages.map(factsOf), skill.statuses ?? [], cap);
}

/** `castBudgetTruncation` from the stage facts themselves: what a program of the skill language is refused by before it is canonical. */
export function castBudgetTruncationOfStages(facts: readonly StageFacts[], statuses: readonly SkillStatusDefinitionV1[], cap: number): string | undefined {
  if (capsExactly(facts, statuses)) return undefined;
  const { demand, limit, unbounded } = castBudgetDemandOf(facts, statuses, cap);
  if (unbounded !== undefined) return `${unbounded}; its releases cannot be bounded against the cast's attack budget (${limit} units left after the plain stages)`;
  if (demand > limit) {
    return `at most ${demand} attack units of follow-up releases (chain members, hooks, sweeps and events; no more than ${CAST_TARGET_BOUND} enemies are taken to be struck) against the ${limit} the cast's one attack budget has left after its plain stages; a release past that is dropped`;
  }
  return undefined;
}
